import useSWR from 'swr';
import { useMemo, useState } from 'react';

export interface PaymentRequest {
  amount: number | string;
  date: string;
  method?: string;
  notes?: string;
}

interface Order {
  id: number;
  documentNumber: string;
  saleDate: string;
  toPay: number;
  toPayAdjusted: number;
  customerName: string;
  paymentsCount?: number;
  futureDocument?: string;
}

export const useCuentasPorCobrar = () => {
  // Estados para modales
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [paymentsOrderId, setPaymentsOrderId] = useState<number | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>({
    amount: 0,
    date: '',
    method: '',
    notes: ''
  });

  // Datos principales con SWR
  const { data, error, mutate } = useSWR('/dashboards/vendor-data');
  console.log('CuentasPorCobrar data:', data);
  const { data: payments } = useSWR(
    paymentsOrderId ? `/orders/payments/${paymentsOrderId}` : null
  );

  // Lógica de cálculo optimizada
  const deuda = useMemo(() => {
    return data?.reduce((total: number, order: Order) => 
      total + Number(order.toPay), 0) || 0;
  }, [data]);

  const deudaNeta = useMemo(() => {
    return data?.reduce((total: number, order: Order) => 
      total + order.toPayAdjusted, 0) || 0;
  }, [data]);

  // Orden seleccionado para solicitud
  const selectedOrder = useMemo(() => 
    data?.find((order: Order) => order.id === selectedOrderId), 
    [data, selectedOrderId]
  );

  // Validaciones
  const exceedsBalance = useMemo(() => {
    if (!selectedOrder || !paymentRequest.amount) return false;
    const balance = Number(selectedOrder.toPayAdjusted ?? selectedOrder.toPay);
    return Number(paymentRequest.amount) > balance;
  }, [selectedOrder, paymentRequest.amount]);

  // Validar si el formulario es válido
  const isFormValid = useMemo(() => {
    return Number(paymentRequest.amount) > 0 && 
           Number(paymentRequest.amount) <= (Number(selectedOrder?.toPayAdjusted ?? selectedOrder?.toPay) || 0) &&
           paymentRequest.method && 
           !exceedsBalance;
  }, [paymentRequest, selectedOrder, exceedsBalance]);

  // Acciones
  const openPaymentRequest = (orderId: number) => {
    setSelectedOrderId(orderId);
    setPaymentRequest({
      amount: '',
      date: '',
      method: '',
      notes: ''
    });
  };

  const closePaymentRequest = () => {
    setSelectedOrderId(null);
    setPaymentRequest({
      amount: '',
      date: '',
      method: '',
      notes: ''
    });
  };

  const openPaymentsHistory = (orderId: number) => {
    setPaymentsOrderId(orderId);
  };

  const closePaymentsHistory = () => {
    setPaymentsOrderId(null);
  };

  const updatePaymentRequest = (field: keyof PaymentRequest, value: string | number) => {
    setPaymentRequest(prev => ({ ...prev, [field]: value }));
  };

  const validatePaymentRequest = () => {
    const amount = Number(paymentRequest.amount);
    if (!paymentRequest.amount || amount <= 0) {
      return 'Monto inválido';
    }
    if (exceedsBalance) {
      return 'El monto excede el saldo pendiente';
    }
    if (!paymentRequest.method) {
      return 'Debe seleccionar un método de pago';
    }
    return null;
  };

  return {
    // Datos
    orders: data as Order[] || [],
    payments,
    deuda,
    deudaNeta,
    
    // Estados
    isLoading: !data && !error,
    isError: !!error,
    selectedOrderId,
    paymentsOrderId,
    selectedOrder,
    paymentRequest,
    
    // Validaciones
    exceedsBalance,
    isFormValid,
    
    // Acciones
    mutate,
    openPaymentRequest,
    closePaymentRequest,
    openPaymentsHistory,
    closePaymentsHistory,
    updatePaymentRequest,
    validatePaymentRequest
  };
};